import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { Eye, Loader2, MoreHorizontal, QrCode, Users as UserIcon } from 'lucide-react';

import type { RecentOrderRow } from './types';
import type { OrderStatus } from '@/lib/api/orders';

type RecentOrdersProps = {
  orders: RecentOrderRow[];
  onViewAll?: () => void;
  onViewOrder?: (orderId: string) => void;
  onUpdateStatus?: (orderId: string, status: OrderStatus) => Promise<void> | void;
  onPrintReceipt?: (orderId: string) => Promise<void> | void;
  statusOptions?: OrderStatus[];
};

const toneClass = (tone: 'up' | 'down' | 'neutral'): string => {
  if (tone === 'up') {
    return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/40';
  }
  if (tone === 'down') {
    return 'bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/40';
  }
  return 'bg-gray-100 text-gray-800 hover:bg-gray-100 dark:bg-gray-800/50 dark:text-gray-300 dark:hover:bg-gray-700/60';
};

const formatStatusLabel = (status: string) =>
  status
    .toLowerCase()
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const formatCountdown = (target: number, now: number): string | null => {
  const delta = target - now;
  if (!Number.isFinite(delta) || delta <= 0) {
    return null;
  }
  const totalSeconds = Math.round(delta / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const totalMinutes = Math.round(totalSeconds / 60);
  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }
  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  if (totalHours < 48) {
    return remainingMinutes > 0 ? `${totalHours}h ${remainingMinutes}m` : `${totalHours}h`;
  }
  const totalDays = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;
  return remainingHours > 0 ? `${totalDays}d ${remainingHours}h` : `${totalDays}d`;
};

const DEFAULT_STATUS_OPTIONS: OrderStatus[] = ['PENDING_PAYMENT', 'PAID', 'ACCEPTED', 'IN_PROGRESS', 'READY', 'SERVED', 'PICKED_UP', 'COMPLETED', 'CANCELLED', 'REFUNDED', 'VOIDED'];

export const RecentOrders: React.FC<RecentOrdersProps> = ({
  orders,
  onViewAll,
  onViewOrder,
  onUpdateStatus,
  onPrintReceipt,
  statusOptions = DEFAULT_STATUS_OPTIONS,
}) => {
  const { t } = useTranslation('dashboard');
  const [now, setNow] = React.useState(() => Date.now());
  const [pendingAction, setPendingAction] = React.useState<{
    orderId: string;
    type: 'status' | 'print';
  } | null>(null);

  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const handleStatusChange = React.useCallback(
    async (orderId: string, status: OrderStatus) => {
      if (!onUpdateStatus) {
        return;
      }
      setPendingAction({ orderId, type: 'status' });
      try {
        await onUpdateStatus(orderId, status);
      } finally {
        setPendingAction((current) => (current?.orderId === orderId ? null : current));
      }
    },
    [onUpdateStatus],
  );

  const handlePrint = React.useCallback(
    async (orderId: string) => {
      if (!onPrintReceipt) {
        return;
      }
      setPendingAction({ orderId, type: 'print' });
      try {
        await onPrintReceipt(orderId);
      } finally {
        setPendingAction((current) => (current?.orderId === orderId ? null : current));
      }
    },
    [onPrintReceipt],
  );

  return (
    <Card className="border border-gray-200/60 bg-white/95 shadow-lg dark:border-gray-700/60 dark:bg-gray-900">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
              {t('recentOrders.title')}
            </CardTitle>
            <CardDescription className="text-sm text-gray-500 dark:text-gray-400">
              {t('recentOrders.description')}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-gray-200/70 bg-gray-50/80 hover:bg-white dark:border-gray-700/60 dark:bg-gray-800/80 dark:hover:bg-gray-700"
            onClick={onViewAll}
          >
            {t('recentOrders.actions.viewAll')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('recentOrders.columns.code')}</TableHead>
              <TableHead>{t('recentOrders.columns.items')}</TableHead>
              <TableHead>{t('recentOrders.columns.table')}</TableHead>
              <TableHead>{t('recentOrders.columns.type')}</TableHead>
              <TableHead>{t('recentOrders.columns.station')}</TableHead>
              <TableHead>{t('recentOrders.columns.status')}</TableHead>
              <TableHead>{t('recentOrders.columns.total')}</TableHead>
              <TableHead>{t('recentOrders.columns.time')}</TableHead>
              <TableHead className="w-[40px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('emptyState.noOrders')}
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => {
                const statusKey = order.status.toLowerCase();
                const statusLabel = t(`orders.status.${statusKey}`, { defaultValue: formatStatusLabel(order.status) });
                const orderTypeLabel =
                  order.orderType === 'qr'
                    ? t('alerts.source.qr')
                    : order.orderType === 'counter'
                      ? t('alerts.source.counter')
                      : t('alerts.source.order', { defaultValue: 'Order' });

                const isStatusPending = pendingAction?.orderId === order.id && pendingAction.type === 'status';
                const isPrintPending = pendingAction?.orderId === order.id && pendingAction.type === 'print';

                const availableStatuses = statusOptions.filter((option) => option !== order.status.toUpperCase());

                return (
                  <TableRow key={order.id}>
                    <TableCell className="font-mono text-sm">{order.code}</TableCell>
                    <TableCell className="max-w-xs truncate" title={order.itemsSummary ?? undefined}>
                      {order.itemsSummary ?? '--'}
                    </TableCell>
                    <TableCell>{order.tableLabel}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-xs">
                        {order.orderType === 'qr' ? (
                          <QrCode className="h-3 w-3 text-emerald-500" />
                        ) : (
                          <UserIcon className="h-3 w-3 text-blue-500" />
                        )}
                        <span className="capitalize">{orderTypeLabel}</span>
                      </span>
                    </TableCell>
                    <TableCell>
                      {order.stationLoad && order.stationLoad.length > 0 ? (
                        <div className="flex flex-wrap items-start gap-2">
                          {order.stationLoad.map((station) => {
                            const countdownLabel =
                              station.throttleEndsAt && station.throttleEndsAt > now
                                ? formatCountdown(station.throttleEndsAt, now)
                                : null;

                            return (
                              <div key={station.id} className="flex flex-col items-start gap-0.5">
                                <Badge
                                  variant={station.flagged ? 'destructive' : 'outline'}
                                  title={`${station.ordersLabel} • ${station.itemsLabel}`}
                                  className="flex items-center gap-1 text-[11px] font-medium"
                                >
                                  <span>{station.label}</span>
                                  <span className="opacity-80">{station.ordersLabel}</span>
                                </Badge>
                                {countdownLabel ? (
                                  <span className="text-[10px] text-muted-foreground/80">
                                    {t('recentOrders.throttleCountdown', {
                                      defaultValue: `Resets in ${countdownLabel}`,
                                      value: countdownLabel,
                                    })}
                                  </span>
                                ) : null}
                                {station.overrideApplied ? (
                                  <span className="text-[10px] text-amber-600 dark:text-amber-300">
                                    {t('kitchen:chips.overrideApplied')}
                                  </span>
                                ) : null}
                              </div>
                            );
                          })}
                          {order.throttleOverrideApplied ? (
                            <Badge
                              variant="outline"
                              className="border-amber-300/60 bg-amber-200/10 text-amber-700 dark:text-amber-200"
                            >
                              {t('kitchen:chips.overrideApplied')}
                            </Badge>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">
                          {t('dashboard:recentOrders.stationLoad.none', { defaultValue: 'No station data' })}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={toneClass(order.statusTone)}
                      >
                        {statusLabel}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium text-gray-900 dark:text-gray-100">{order.totalLabel}</TableCell>
                    <TableCell className="text-gray-500 dark:text-gray-400">{order.timeLabel}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onViewOrder?.(order.id)}>
                            <Eye className="mr-2 h-4 w-4" /> {t('recentOrders.actions.viewDetails')}
                          </DropdownMenuItem>
                          {onUpdateStatus ? (
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger disabled={isStatusPending || availableStatuses.length === 0}>
                                {isStatusPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                {t('recentOrders.actions.updateStatus')}
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent>
                                <DropdownMenuLabel>{t('recentOrders.actions.updateStatus')}</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {availableStatuses.length === 0 ? (
                                  <DropdownMenuItem disabled>
                                    {t('recentOrders.actions.updateStatus')}
                                  </DropdownMenuItem>
                                ) : (
                                  availableStatuses.map((statusOption) => {
                                    const statusName = t(`orders.status.${statusOption.toLowerCase()}`, {
                                      defaultValue: formatStatusLabel(statusOption),
                                    });
                                    return (
                                      <DropdownMenuItem
                                        key={statusOption}
                                        disabled={isStatusPending}
                                        onClick={() => handleStatusChange(order.id, statusOption)}
                                      >
                                        {statusName}
                                      </DropdownMenuItem>
                                    );
                                  })
                                )}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                          ) : (
                            <DropdownMenuItem disabled>{t('recentOrders.actions.updateStatus')}</DropdownMenuItem>
                          )}
                          {onPrintReceipt ? (
                            <DropdownMenuItem disabled={isPrintPending} onClick={() => handlePrint(order.id)}>
                              {isPrintPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                              {t('recentOrders.actions.printReceipt')}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem disabled>{t('recentOrders.actions.printReceipt')}</DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
